/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { EcsperAuthProvider, useEcsperAuth } from '@ecsper/auth';
import type { AmbiguousOfficeInfo } from '@ecsper/types';
import { type OfficeOption, OfficePicker } from '@ecsper/ui-panels';
import type { ReactElement, ReactNode } from 'react';

import type { AppContext } from '../bootstrap.js';
import { ReportPicker } from '../components/ReportPicker.js';
import { AppShell } from './AppShell.js';
import { Stage6ToastBridge } from './stage6-toast-bridge.js';

export interface AuthGateProps {
  readonly ctx: AppContext;
  /** From URL or chosen in {@link ReportPicker} when absent from the URL. */
  readonly reportId: string | null;
  /** Matter scope for listing reports (URL `matterId` query). */
  readonly matterId: string | null;
  readonly onReportSelected: (reportId: string) => void;
}

function mapAmbiguousOffices(offices: readonly AmbiguousOfficeInfo[]): OfficeOption[] {
  return offices.map((o) => ({
    officeId: o.office_id,
    officeName: o.office_name,
    organisationName: '—',
    role: 'Member',
  }));
}

function AuthGateInner({ ctx, reportId, matterId, onReportSelected }: AuthGateProps): ReactElement {
  const { state, selectOffice } = useEcsperAuth();

  let body: ReactElement;
  if (state.status === 'office_picker' && state.officeOptions !== null) {
    body = (
      <OfficePicker
        open
        offices={mapAmbiguousOffices(state.officeOptions)}
        onSelect={(officeId) => {
          void selectOffice(officeId);
        }}
        onCancel={() => {
          /* Picker is required to resolve AMBIGUOUS_OFFICE; closing leaves user signed out. */
        }}
      />
    );
  } else if (state.status === 'error' && state.error !== null) {
    body = (
      <div role="alert">
        {state.error.title}: {state.error.detail}
      </div>
    );
  } else if (state.status !== 'authenticated') {
    body = <div role="status">Signing in…</div>;
  } else if (reportId === null || reportId === '') {
    body = <ReportPicker ctx={ctx} matterId={matterId} onReportSelected={onReportSelected} />;
  } else {
    body = <AppShell ctx={ctx} reportId={reportId} matterId={matterId ?? ''} />;
  }

  return (
    <>
      {body}
      <Stage6ToastBridge />
    </>
  );
}

/** Root authentication and routing around {@link AppShell}. */
export function AuthGateTree(props: AuthGateProps): ReactElement {
  return (
    <EcsperAuthProvider service={props.ctx.auth}>
      <AuthGateInner {...props} />
    </EcsperAuthProvider>
  );
}

/** Wraps children with {@link EcsperAuthProvider} for tests or extensions. */
export function AuthProviderShell(props: {
  readonly service: AppContext['auth'];
  readonly children: ReactNode;
}): ReactElement {
  return <EcsperAuthProvider service={props.service}>{props.children}</EcsperAuthProvider>;
}
